import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { registerQuoteFonts } from "@/lib/pdfFont";
import { NUM, fmtDate, unitLabel, docFileName } from "@/lib/format";
import { COMPANY } from "@/lib/company";
import { loadCompanyLogo, logoAspect } from "@/lib/branding";
import { itemDisplayName } from "@/components/QuoteTemplate";
import { DEFAULT_PDF_OPTS, DEFAULT_TITLE, DEFAULT_DELIVERY, formTotals, itemCost, lineTotal } from "@/components/orders/orderFormUtils";

const NAVY = [11, 25, 44];
const INK = [15, 23, 42];
const ORANGE = [234, 88, 32];
const ORANGE_D = [180, 60, 14];
const SLATE = [100, 116, 139];
const MUTED = [148, 163, 184];
const LINE = [203, 213, 225];
const WASH = [248, 250, 252];
const HEAD_BG = [241, 245, 249];
const WHITE = [255, 255, 255];
const money = (v) => `${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v || 0))} ₺`;

/** Sipariş / Teklif İsteme Formu — hızlı satış fişiyle aynı sade şablon (beyaz zemin, ince çizgili tablo, imzasız). */
export async function buildOrderFormPdf(form, opts = {}) {
  const o = { ...DEFAULT_PDF_OPTS, ...(form.pdf_opts || {}), ...opts };
  const pdf = new jsPDF("p", "mm", "a4");
  const F = await registerQuoteFonts(pdf);
  const logo = await loadCompanyLogo();
  const AR = logoAspect();
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const M = 15, W = pageW - M * 2, FOOT = 16, HEAD1 = 44, HEAD2 = 24;
  const T = (font, style, size, color) => { pdf.setFont(font, style); pdf.setFontSize(size); pdf.setTextColor(...color); };
  const fill = (c) => pdf.setFillColor(...c);
  const stroke = (c, w = 0.3) => { pdf.setDrawColor(...c); pdf.setLineWidth(w); };
  const fit = (text, maxW) => {
    let raw = String(text ?? ""), out = raw;
    while (pdf.getTextWidth(out) > maxW && raw.length > 2) { raw = raw.slice(0, -1); out = raw.trimEnd() + "…"; }
    return out;
  };

  const items = form.items || [];
  const totals = formTotals(items);
  const sup = String(form.supplier_title || "").trim();
  const title = ((form.title || "").trim() || DEFAULT_TITLE).toLocaleUpperCase("tr");
  const formNo = String(form.form_no || "TASLAK");
  const dateStr = fmtDate(form.date);
  const brands = form.brands && form.brands.length ? form.brands : [...new Set(items.map((i) => (i.brand || "").trim()).filter(Boolean))];
  const drawn = new Set();

  const drawLogo = (x, y, h) => {
    const lw = Math.min(h * AR, 60);
    if (logo) { try { pdf.addImage(logo, "PNG", x, y, lw, h); return; } catch { /* fallthrough */ } }
    T(F.head, "bold", h * 0.55, NAVY); pdf.text(COMPANY.name, x, y + h * 0.7);
  };

  const drawHeader = (pageNo) => {
    if (drawn.has(pageNo)) return;
    drawn.add(pageNo);
    fill(ORANGE); pdf.rect(0, 0, pageW, 1.1, "F");
    if (pageNo === 1) {
      drawLogo(M, 10, 13);
      T(F.body, "normal", 6.9, SLATE);
      pdf.text(fit(COMPANY.address, 118), M, 28.6);
      pdf.text(fit(`${COMPANY.email}   ·   V.D. ${COMPANY.taxOffice}   ·   TCKN ${COMPANY.tckn}`, 118), M, 32.4);
      T(F.body, "bold", 6.9, NAVY); pdf.text(`Tel: ${COMPANY.phone}`, M, 36.2);
      // Başlık: "SİPARİŞ TALEP FORMU" / "FİYAT TALEP FORMU" (kullanıcı seçer) — sağa yaslı, turuncu alt çizgi
      let fs = 18.5; T(F.head, "bold", fs, NAVY);
      while (pdf.getTextWidth(title) > 112 && fs > 9) { fs -= 0.5; T(F.head, "bold", fs, NAVY); }
      const titleW = pdf.getTextWidth(title);
      pdf.text(title, pageW - M, 17.4, { align: "right" });
      fill(ORANGE); pdf.rect(pageW - M - titleW, 20.4, titleW, 1, "F");
      const metaRows = [["Form No", formNo, F.mono, 8.2], ["Tarih", dateStr, F.body, 8]];
      let my = 26;
      metaRows.forEach(([l, v, font, mfs]) => {
        T(font, "bold", mfs, INK); const vw = pdf.getTextWidth(String(v));
        pdf.text(String(v), pageW - M, my, { align: "right" });
        T(F.body, "normal", 6.9, SLATE); pdf.text(l, pageW - M - vw - 2.5, my, { align: "right" });
        my += 4.7;
      });
      stroke(NAVY, 0.45); pdf.line(M, HEAD1 - 3, pageW - M, HEAD1 - 3);
    } else {
      drawLogo(M, 7, 9.5);
      T(F.head, "bold", 11, NAVY); pdf.text(fit(title, 110), pageW - M, 12, { align: "right", charSpace: 0.6 });
      T(F.mono, "bold", 7, SLATE); pdf.text(`${formNo}  ·  ${dateStr}  ·  devam`, pageW - M, 16.6, { align: "right" });
      stroke(LINE, 0.3); pdf.line(M, HEAD2 - 3, pageW - M, HEAD2 - 3);
    }
  };

  // Bilgi şeridi: Tedarikçi (seçildiyse, uzunsa "…" ile kısaltılır) · Marka · Talep Eden — hücreler dikey çizgiyle ayrılır
  const infoY = HEAD1 + 2, infoH = 16;
  const cells = [];
  if (sup) cells.push(["TEDARİKÇİ", sup]);
  cells.push(["MARKA", brands.length ? brands.join(", ") : "-"]);
  cells.push(["TALEP EDEN", `${COMPANY.salesRep}   ·   ${COMPANY.phone}`]);
  const fr = sup ? [0.38, 0.22, 0.40] : [0.5, 0.5];
  const drawInfo = () => {
    fill(WHITE); stroke(LINE, 0.35); pdf.roundedRect(M, infoY, W, infoH, 2, 2, "FD");
    fill(ORANGE); pdf.roundedRect(M + 1.4, infoY + 4, 1.2, infoH - 8, 0.6, 0.6, "F");
    let cx = M;
    cells.forEach(([l, v], i) => {
      const cw = W * fr[i], px = cx + (i === 0 ? 6 : 5);
      T(F.body, "bold", 5.9, MUTED); pdf.text(l, px, infoY + 5.7, { charSpace: 0.45 });
      T(F.body, "bold", 8.6, INK); pdf.text(fit(v, cw - 10), px, infoY + 12);
      cx += cw;
      if (i < cells.length - 1) { stroke(LINE, 0.3); pdf.line(cx, infoY + 3, cx, infoY + infoH - 3); }
    });
  };

  // ---------- Kalem tablosu (ızgara: sütunlar arası dikey çizgiler, satır çizgileri) ----------
  const base = o.showPrices ? 7.6 : 8.6;
  const colDefs = [{ key: "no", label: "#", w: 8, align: "center", style: { textColor: MUTED } }];
  if (o.showCodes) colDefs.push({ key: "code", label: "Ürün Kodu", w: o.showPrices ? 24 : 26, align: "left", style: { font: F.mono, fontStyle: "bold", textColor: NAVY, fontSize: base - 0.8 } });
  colDefs.push({ key: "name", label: "Ürün / Malzeme", w: 0, align: "left", style: { fontStyle: "bold" } });
  if (o.showPrices) {
    colDefs.push({ key: "qtyu", label: "Miktar", w: 22, align: "center", style: { fontStyle: "bold" } });
    colDefs.push({ key: "list", label: "Liste Fiyatı", w: 21, align: "right", style: { textColor: SLATE } });
    colDefs.push({ key: "disc", label: "İsk.", w: 12, align: "center", style: { textColor: ORANGE, fontStyle: "bold", fontSize: base - 0.5 } });
    colDefs.push({ key: "cost", label: "Net Maliyet", w: 21, align: "right" });
    colDefs.push({ key: "tot", label: "Tutar", w: 24, align: "right", style: { fontStyle: "bold", textColor: NAVY } });
  } else {
    colDefs.push({ key: "qty", label: "Miktar", w: 15, align: "center", style: { fontStyle: "bold" } });
    colDefs.push({ key: "unit", label: "Birim", w: 14, align: "center", style: { textColor: SLATE, fontSize: base - 1 } });
    if (o.showBlankCols) colDefs.push({ key: "offer", label: "Teklif Birim Fiyatı", w: 27, align: "center" });
    if (o.showTermin) colDefs.push({ key: "termin", label: "Termin", w: 19, align: "center" });
  }
  const fixedW = colDefs.reduce((s, c) => s + c.w, 0);
  colDefs.find((c) => c.key === "name").w = W - fixedW;
  const head = [colDefs.map((c) => ({ content: c.label.toLocaleUpperCase("tr-TR"), styles: { halign: c.align } }))];
  const nameOf = (it) => {
    const subs = o.showNotes && String(it.note || "").trim() ? [`Açıklama: ${String(it.note).trim()}`] : [];
    return [itemDisplayName(it, { showBrand: o.showBrand }), ...subs].join("\n");
  };
  const body = items.map((it, i) => colDefs.map((c) => {
    switch (c.key) {
      case "no": return String(i + 1);
      case "code": return String(it.code || "").trim() || "-";
      case "name": return nameOf(it);
      case "qty": return NUM(it.qty);
      case "qtyu": return `${NUM(it.qty)} ${unitLabel(it.unit)}`;
      case "unit": return unitLabel(it.unit);
      case "list": return money(it.list_price);
      case "disc": return Number(it.cost_discount) ? `%${NUM(it.cost_discount)}` : "-";
      case "cost": return money(itemCost(it));
      case "tot": return money(lineTotal(it));
      default: return "";
    }
  }));
  const colStyles = {};
  colDefs.forEach((c, i) => { colStyles[i] = { cellWidth: c.w, halign: c.align, ...(c.style || {}) }; });
  const nameIdx = colDefs.findIndex((c) => c.key === "name");
  const lastCol = colDefs.length - 1;
  autoTable(pdf, {
    head,
    body: body.length ? body : [[{ content: "Kalem bulunmuyor", colSpan: colDefs.length, styles: { halign: "center", textColor: SLATE } }]],
    startY: infoY + infoH + 6,
    margin: { left: M, right: M, top: HEAD2 + 2, bottom: FOOT + 8 },
    tableWidth: W,
    theme: "grid",
    rowPageBreak: "avoid",
    styles: { font: F.body, fontSize: base, cellPadding: { top: 2.1, bottom: 2.1, left: 2, right: 2 }, textColor: INK, lineColor: LINE, lineWidth: 0.2, overflow: "linebreak", valign: "middle", minCellHeight: 8.4 },
    headStyles: { font: F.body, fontStyle: "bold", fillColor: HEAD_BG, textColor: NAVY, fontSize: 6.9, valign: "middle", minCellHeight: 8.4, cellPadding: { top: 2.2, bottom: 2.2, left: 2, right: 2 }, lineColor: LINE, lineWidth: 0.2 },
    alternateRowStyles: { fillColor: WASH },
    columnStyles: colStyles,
    willDrawCell: (d) => { if (d.section === "body" && d.column.index === nameIdx && typeof d.cell.raw === "string") { const parts = d.cell.raw.split("\n"); d.cell._name = parts[0]; d.cell._subs = parts.slice(1); d.cell.text = []; } },
    didDrawCell: (d) => {
      if (d.section === "head" && d.column.index === lastCol) { fill(NAVY); pdf.rect(M, d.cell.y + d.cell.height - 0.5, W, 0.5, "F"); }
      if (d.cell._name == null) return;
      const { x, y, width, height } = d.cell; const padL = d.cell.padding("left"), avail = width - padL - d.cell.padding("right");
      const subFs = base - 1.1;
      T(F.body, "bold", base, INK); const nameLines = pdf.splitTextToSize(d.cell._name, avail);
      T(F.body, "bold", subFs, ORANGE_D); const subLines = d.cell._subs.flatMap((s) => pdf.splitTextToSize(s, avail));
      const lh = base * 0.42, slh = subFs * 0.46;
      let ty = y + (height - (nameLines.length * lh + subLines.length * slh)) / 2 + lh * 0.78;
      T(F.body, "bold", base, INK); nameLines.forEach((ln) => { pdf.text(ln, x + padL, ty); ty += lh; });
      // Açıklama varsa belirgin: turuncu-koyu, kalın
      T(F.body, "bold", subFs, ORANGE_D); subLines.forEach((ln) => { pdf.text(ln, x + padL, ty); ty += slh; });
    },
    didDrawPage: (d) => { drawHeader(d.pageNumber); if (d.pageNumber === 1) drawInfo(); },
  });

  // ---------- Teslimat / not (sol) + tahmini maliyet (sağ, yalnız fiyatlar açıksa) ----------
  let y = pdf.lastAutoTable.finalY + 6;
  const bottomLimit = pageH - FOOT - 4;
  const rightW = o.showPrices ? 82 : 0, rightX = M + W - rightW, leftW = o.showPrices ? W - rightW - 8 : W;
  const noteTxt = String(form.note || "").trim();
  const delivTxt = String(form.delivery_note || "").trim() || DEFAULT_DELIVERY(COMPANY);
  T(F.body, "normal", 8.2, INK); const noteLines = noteTxt ? pdf.splitTextToSize(noteTxt, leftW - 11) : [];
  const noteH = noteLines.length ? 8 + noteLines.length * 4 + 2 : 0;
  T(F.body, "normal", 7.6, SLATE); const deliv = pdf.splitTextToSize(delivTxt, leftW - 11);
  const delivH = 8 + deliv.length * 3.7 + 2;
  const leftH = noteH + (noteH ? 4 : 0) + delivH;
  const rh = 5.2, boxH = 15, totalsH = o.showPrices ? 5 + 2 * rh + 2 + boxH : 0;
  const blockH = Math.max(leftH, totalsH);
  if (y + blockH > bottomLimit) { pdf.addPage(); drawHeader(pdf.getNumberOfPages()); y = HEAD2 + 2; }

  let ly = y;
  if (noteH) {
    fill(WHITE); stroke(LINE, 0.35); pdf.roundedRect(M, ly, leftW, noteH, 2, 2, "FD");
    fill(ORANGE); pdf.roundedRect(M + 1.4, ly + 3, 1.2, noteH - 6, 0.6, 0.6, "F");
    T(F.body, "bold", 6.2, ORANGE); pdf.text("NOT / AÇIKLAMA", M + 6, ly + 5.2, { charSpace: 0.45 });
    T(F.body, "normal", 8.2, INK); pdf.text(noteLines, M + 6, ly + 10.4, { lineHeightFactor: 1.38 });
    ly += noteH + 4;
  }
  fill(WASH); stroke(LINE, 0.35); pdf.roundedRect(M, ly, leftW, delivH, 2, 2, "FD");
  T(F.body, "bold", 6.2, NAVY); pdf.text("TESLİMAT", M + 6, ly + 5.2, { charSpace: 0.45 });
  T(F.body, "normal", 7.6, SLATE); pdf.text(deliv, M + 6, ly + 10, { lineHeightFactor: 1.38 });

  if (o.showPrices) {
    let py = y;
    T(F.body, "bold", 6, MUTED); pdf.text("TAHMİNİ MALİYET", rightX + 5, py + 2, { charSpace: 0.5 }); py += 4.2;
    [["Ara Toplam (KDV hariç)", money(totals.net)], ["KDV", money(totals.kdv)]].forEach(([l, v]) => {
      T(F.body, "normal", 7.4, SLATE); pdf.text(l, rightX + 5, py + 2.6);
      T(F.body, "bold", 7.6, INK); pdf.text(v, rightX + rightW - 5, py + 2.6, { align: "right" });
      py += rh;
    });
    py += 2;
    fill(WASH); stroke(NAVY, 0.6); pdf.roundedRect(rightX, py, rightW, boxH, 2, 2, "FD");
    fill(ORANGE); pdf.roundedRect(rightX + 3, py + 4, 1.3, boxH - 8, 0.6, 0.6, "F");
    T(F.body, "bold", 6.6, NAVY); pdf.text("TOPLAM MALİYET", rightX + 7.5, py + boxH / 2 - 1.6, { charSpace: 0.6 });
    T(F.body, "normal", 6, SLATE); pdf.text("KDV dahil · tahmini", rightX + 7.5, py + boxH / 2 + 3.4);
    let afs = 14; T(F.head, "bold", afs, NAVY);
    const amt = money(totals.total);
    while (pdf.getTextWidth(amt) > rightW - 44 && afs > 9) { afs -= 0.5; T(F.head, "bold", afs, NAVY); }
    pdf.text(amt, rightX + rightW - 5, py + boxH / 2 + 2.2, { align: "right" });
  }
  y += blockH + 8;

  // ---------- Alt bilgi (tüm sayfalar) ----------
  const n = pdf.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    pdf.setPage(i);
    const fy = pageH - FOOT + 1;
    stroke(LINE, 0.3); pdf.line(M, fy, pageW - M, fy);
    fill(ORANGE); pdf.rect(M, fy - 0.45, 12, 0.9, "F");
    T(F.body, "bold", 7, NAVY); pdf.text(COMPANY.name, M, fy + 4.8);
    T(F.body, "normal", 6.2, SLATE); pdf.text(fit(COMPANY.address, 80), M, fy + 8.4);
    pdf.text(`Sayfa ${i} / ${n}`, pageW / 2, fy + 8.4, { align: "center" });
    T(F.body, "bold", 6.6, NAVY); pdf.text(COMPANY.email, pageW - M, fy + 4.8, { align: "right" });
    T(F.body, "normal", 6.2, SLATE); pdf.text(`Tel: ${COMPANY.phone}   ·   V.D. ${COMPANY.taxOffice}`, pageW - M, fy + 8.4, { align: "right" });
  }
  return pdf;
}

export const orderFormFilename = (form) => docFileName(form.supplier_title || "Sipariş Talep Formu", form.form_no || "Taslak");
